import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { addFoodEntry, deleteFoodEntry, updateFoodEntry, addWaterEntry, getNutritionSummary } from '../lib/api';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { FoodScanner } from '../components/FoodScanner';
import { BarcodeScannerSheet } from '../components/BarcodeScannerSheet';
import { NutritionTrendSection } from '../components/NutritionTrendSection';
import { TDEEInfoButton } from '../components/TDEEInfoButton';
import { CalendarStrip } from '../components/CalendarStrip';
import { WeatherTipsCard } from '../components/WeatherTipsCard';
import { EnergyBalanceCard } from '../components/EnergyBalanceCard';
import { TalerCard } from '../components/TalerCard';
import { TalerCalibrationCard } from '../components/TalerCalibrationCard';
import { HabitMealChecklist } from '../components/HabitMealChecklist';
import { QuickLogDrawer } from '../components/QuickLogDrawer';
import { PriceCheckSheet } from '../components/PriceCheckSheet';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useSmartComment } from '../hooks/useSmartComment';
import {
    Apple, Plus, Droplets, Flame, Beef, Wheat, Droplet, Trash2,
    Coffee, Sun, Sunset, Sparkles, Camera, Pencil, Check, X, ChevronDown, Loader2, GlassWater, Dumbbell, ScanLine, Tag
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Nutrition() {
    const { t, language } = useLanguage();
    const { user } = useAuth();
    const [summary, setSummary] = useState(null);
    const [talerBump, setTalerBump] = useState(0);
    const [loading, setLoading] = useState(true);
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [quickLogOpen, setQuickLogOpen] = useState(false);
    const [scannerOpen, setScannerOpen] = useState(false);
    const [barcodeOpen, setBarcodeOpen] = useState(false);
    const [priceCheckOpen, setPriceCheckOpen] = useState(false);
    const [editingEntry, setEditingEntry] = useState(null);
    const [expandedMealType, setExpandedMealType] = useState(null);
    const [sportCalories, setSportCalories] = useState(0);

    const mealTypes = [
        { value: 'breakfast', label: language === 'de' ? 'Frühstück' : 'Breakfast', icon: Coffee, color: 'icon-fitness', bg: 'module-fitness' },
        { value: 'lunch', label: language === 'de' ? 'Mittagessen' : 'Lunch', icon: Sun, color: 'icon-nutrition', bg: 'module-nutrition' },
        { value: 'dinner', label: language === 'de' ? 'Abendessen' : 'Dinner', icon: Sunset, color: 'icon-sleep', bg: 'module-sleep' },
        { value: 'snack', label: language === 'de' ? 'Snack' : 'Snack', icon: Apple, color: 'icon-travel', bg: 'module-travel' },
    ];

    const waterAmounts = [150, 250, 330, 500];

    const drinks = [
        { id: 'water_250', name: language === 'de' ? 'Wasser' : 'Water', icon: '💧', ml: 250, kcal: 0, isWater: true },
        { id: 'water_500', name: language === 'de' ? 'Wasser 0.5L' : 'Water 0.5L', icon: '💧', ml: 500, kcal: 0, isWater: true },
        { id: 'coffee', name: 'Kaffee', icon: '☕', ml: 150, kcal: 5, isWater: false },
        { id: 'coffee_milk', name: language === 'de' ? 'Kaffee m. Milch' : 'Coffee w/ Milk', icon: '☕', ml: 200, kcal: 45, isWater: false },
        { id: 'cappuccino', name: 'Cappuccino', icon: '☕', ml: 200, kcal: 80, isWater: false },
        { id: 'tea', name: language === 'de' ? 'Tee' : 'Tea', icon: '🍵', ml: 250, kcal: 2, isWater: true },
        { id: 'juice', name: language === 'de' ? 'Saft' : 'Juice', icon: '🧃', ml: 200, kcal: 90, isWater: false },
        { id: 'softdrink', name: 'Softdrink', icon: '🥤', ml: 330, kcal: 140, isWater: false },
        { id: 'wine_red', name: language === 'de' ? 'Rotwein' : 'Red Wine', icon: '🍷', ml: 150, kcal: 125, isAlcohol: true },
        { id: 'wine_white', name: language === 'de' ? 'Weisswein' : 'White Wine', icon: '🥂', ml: 150, kcal: 120, isAlcohol: true },
        { id: 'beer', name: language === 'de' ? 'Bier' : 'Beer', icon: '🍺', ml: 330, kcal: 150, isAlcohol: true },
        { id: 'smoothie', name: 'Smoothie', icon: '🥤', ml: 300, kcal: 160, isWater: false },
    ];

    const [drinkLoading, setDrinkLoading] = useState(null);
    const [quickLogMealType, setQuickLogMealType] = useState(null);
    const { fetchComment } = useSmartComment();

    const loadSummary = useCallback(async () => {
        try {
            setLoading(true);
            const dateStr = selectedDate.toISOString().split('T')[0];
            const response = await getNutritionSummary(dateStr);
            setSummary(response.data);
        } catch (error) {
            console.error('Failed to load nutrition:', error);
            toast.error(t('error_loading'));
        } finally {
            setLoading(false);
        }
        // Load active calories (workouts + steps) for the SELECTED date
        try {
            const dateStr = selectedDate.toISOString().split('T')[0];
            const res = await axios.get(`${API}/fitness/active-calories?date=${dateStr}`);
            setSportCalories(res.data?.total_kcal || 0);
        } catch(e) { setSportCalories(0); }
    }, [selectedDate, t]);

    useEffect(() => {
        loadSummary();
    }, [loadSummary]);

    const handleFoodScanned = async (foodData) => {
        try {
            await addFoodEntry({
                name: foodData.name,
                meal_type: foodData.meal_type || 'snack',
                calories: foodData.calories,
                protein: foodData.protein,
                carbs: foodData.carbs,
                fat: foodData.fat,
                fiber: foodData.fiber || 0,
                notes: foodData.source === 'barcode' ? `EAN: ${foodData.ean}` : 'Foto-Analyse',
                logged_at: selectedDate.toISOString()
            });
            toast.success(`${t('meal_saved')} +5 ${t('points')}`);
            loadSummary();
        } catch (error) {
            toast.error(t('error_saving'));
        }
    };

    const handleDeleteFood = async (id) => {
        try {
            await deleteFoodEntry(id);
            toast.success(language === 'de' ? 'Gelöscht' : 'Deleted');
            loadSummary();
        } catch (error) {
            toast.error(t('error_deleting'));
        }
    };

    const handleUpdateFood = async (id, data) => {
        try {
            await updateFoodEntry(id, data);
            toast.success(language === 'de' ? 'Aktualisiert' : 'Updated');
            setEditingEntry(null);
            loadSummary();
        } catch (error) {
            toast.error(t('error_saving'));
        }
    };

    const handleAddWater = async (amount) => {
        try {
            await addWaterEntry({ amount_ml: amount, logged_at: selectedDate.toISOString() });
            toast.success(`+${amount}ml 💧`);
            loadSummary();
        } catch (error) {
            toast.error(t('error_saving'));
        }
    };

    const handleLogDrink = async (drink) => {
        setDrinkLoading(drink.id);
        try {
            if (drink.isWater || drink.ml > 0) {
                await addWaterEntry({ amount_ml: drink.ml, logged_at: selectedDate.toISOString() });
            }
            if (drink.kcal > 0) {
                await addFoodEntry({
                    name: drink.name,
                    meal_type: 'snack',
                    calories: drink.kcal,
                    protein: 0,
                    carbs: drink.kcal > 50 ? Math.round(drink.kcal * 0.9 / 4) : 0,
                    fat: 0,
                    notes: drink.isAlcohol ? 'Alkohol' : 'Getränk',
                    logged_at: selectedDate.toISOString()
                });
            }
            const kcalText = drink.kcal > 0 ? ` +${drink.kcal} kcal` : '';
            toast.success(`${drink.icon} ${drink.name} (${drink.ml}ml${kcalText})`);
            loadSummary();
        } catch (error) {
            toast.error(t('error_saving'));
        } finally {
            setDrinkLoading(null);
        }
    };

    const calorieGoal = summary?.goals?.calorie_goal || 2000;
    const waterGoal = summary?.goals?.water_goal_ml || 2500;
    const calorieProgress = summary ? (summary.totals.calories / calorieGoal) * 100 : 0;
    const waterProgress = summary ? (summary.totals.water_ml / waterGoal) * 100 : 0;

    const groupedMeals = summary?.food_entries?.reduce((acc, entry) => {
        const type = entry.meal_type || 'snack';
        if (!acc[type]) acc[type] = [];
        acc[type].push(entry);
        return acc;
    }, {}) || {};

    const getMealIcon = (type) => {
        const meal = mealTypes.find(m => m.value === type);
        return meal ? meal.icon : Apple;
    };

    const isToday = selectedDate.toDateString() === new Date().toDateString();

    return (
        <div className="pb-24 lg:pb-8" data-testid="nutrition-page">
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="max-w-4xl mx-auto space-y-4"
            >
                {/* Sticky Header with Calendar Strip */}
                <div className="sticky top-0 z-20 bg-background/95 backdrop-blur-lg -mx-4 px-4 pt-4 pb-3 border-b border-border">
                    <div className="flex items-center justify-between mb-3">
                        <h1 className="font-display text-xl lg:text-2xl font-bold flex items-center gap-2">
                            <Apple className="w-6 h-6 icon-nutrition" />
                            {t('nutrition_title')}
                        </h1>
                        <div className="flex gap-2">
                            <Button 
                                variant="outline" 
                                size="icon"
                                className="rounded-full h-10 w-10"
                                onClick={() => setBarcodeOpen(true)}
                                title={language === 'de' ? 'Barcode scannen' : 'Scan barcode'}
                                aria-label={language === 'de' ? 'Barcode scannen' : 'Scan barcode'}
                                data-testid="nutrition-barcode-btn"
                            >
                                <ScanLine className="w-5 h-5" />
                            </Button>
                            <Button 
                                variant="outline" 
                                size="icon"
                                className="rounded-full h-10 w-10"
                                onClick={() => setScannerOpen(true)}
                                title={language === 'de' ? 'Mahlzeit fotografieren' : 'Photograph meal'}
                                aria-label={language === 'de' ? 'Mahlzeit fotografieren' : 'Photograph meal'}
                            >
                                <Camera className="w-5 h-5" />
                            </Button>
                            <Button
                                variant="outline"
                                size="icon"
                                className="rounded-full h-10 w-10 border-amber-300 text-amber-700 hover:bg-amber-50"
                                onClick={() => setPriceCheckOpen(true)}
                                title={language === 'de' ? 'Preis-Check: Taler & Kalorien vor dem Essen prüfen' : 'Price check: Taler & calories before eating'}
                                aria-label={language === 'de' ? 'Preis-Check' : 'Price check'}
                                data-testid="nutrition-price-check-btn"
                            >
                                <Tag className="w-5 h-5" />
                            </Button>
                            <Button 
                                className="rounded-full h-10 px-4 bg-primary text-primary-foreground hover:bg-primary/90"
                                onClick={() => { setQuickLogMealType(null); setQuickLogOpen(true); }}
                            >
                                <Plus className="w-5 h-5 mr-1" />
                                {language === 'de' ? 'Eintrag' : 'Add'}
                            </Button>
                        </div>
                    </div>
                    
                    {/* Calendar Strip */}
                    <CalendarStrip 
                        selectedDate={selectedDate}
                        onDateChange={setSelectedDate}
                    />
                </div>

                <div className="px-4 space-y-4">
                    {/* Weather-aware Nanouk tips */}
                    <WeatherTipsCard category="nutrition" />

                    {/* Taler budget (only when enabled in settings) */}
                    <TalerCalibrationCard onApplied={() => setTalerBump(b => b + 1)} />
                    <TalerCard date={selectedDate} refreshKey={`${summary?.totals?.calories || 0}-${sportCalories}-${talerBump}`} />

                    {/* Quick Stats Row */}
                    <div className="grid grid-cols-2 gap-3">
                        {/* Calories Card with Sport Bonus */}
                        <Card className="module-fitness border">
                            <CardContent className="p-4">
                                <div className="flex items-center gap-2 mb-2">
                                    <Flame className="w-5 h-5 icon-fitness" />
                                    <span className="text-sm text-muted-foreground">{t('calories')}</span>
                                </div>
                                <div className="text-2xl font-bold">
                                    {summary?.totals?.calories || 0}
                                    <span className="text-sm text-muted-foreground font-normal"> / {calorieGoal + sportCalories}</span>
                                </div>
                                <Progress value={Math.min((summary?.totals?.calories || 0) / (calorieGoal + sportCalories) * 100, 100)} className="h-2 mt-2" />
                                {sportCalories > 0 && (
                                    <p className="text-[10px] text-green-600 dark:text-green-400 mt-1">+{sportCalories} kcal {language === 'de' ? 'durch Sport' : 'from exercise'}</p>
                                )}
                            </CardContent>
                        </Card>

                        {/* Water Card */}
                        <Card className="module-travel border">
                            <CardContent className="p-4">
                                <div className="flex items-center gap-2 mb-2">
                                    <Droplets className="w-5 h-5 icon-travel" />
                                    <span className="text-sm text-muted-foreground">{t('water')}</span>
                                </div>
                                <div className="text-2xl font-bold">
                                    {((summary?.totals?.water_ml || 0) / 1000).toFixed(1)}L
                                    <span className="text-sm text-muted-foreground font-normal"> / {waterGoal/1000}L</span>
                                </div>
                                <Progress value={Math.min(waterProgress, 100)} className="h-2 mt-2" />
                            </CardContent>
                        </Card>
                    </div>

                    {/* Macros Row with targets */}
                    {(() => {
                        const weight = user?.weight || 75;
                        const level = user?.fitness_level || 'beginner';
                        const proteinPerKg = level === 'advanced' ? 2.0 : level === 'intermediate' ? 1.6 : 1.2;
                        const customP = user?.protein_goal_g;
                        const customC = user?.carbs_goal_g;
                        const customF = user?.fat_goal_g;
                        const recProtein = (customP && customP > 0) ? customP : Math.round(weight * proteinPerKg);
                        const recFat = (customF && customF > 0) ? customF : Math.round(weight * 1.0);
                        const recCarbs = (customC && customC > 0) ? customC : Math.max(Math.round((calorieGoal - recProtein * 4 - recFat * 9) / 4), 100);
                        return (
                            <div className="grid grid-cols-3 gap-2">
                                {[
                                    { icon: <Beef className="w-4 h-4 mx-auto icon-health mb-1" />, actual: Math.round(summary?.totals?.protein || 0), target: recProtein, label: t('protein'), bg: 'module-health' },
                                    { icon: <Wheat className="w-4 h-4 mx-auto icon-fitness mb-1" />, actual: Math.round(summary?.totals?.carbs || 0), target: Math.max(recCarbs, 100), label: t('carbs'), bg: 'module-fitness' },
                                    { icon: <Droplet className="w-4 h-4 mx-auto icon-nutrition mb-1" />, actual: Math.round(summary?.totals?.fat || 0), target: recFat, label: t('fat'), bg: 'module-nutrition' },
                                ].map((m) => {
                                    const pct = Math.min(m.actual / m.target * 100, 100);
                                    return (
                                        <div key={m.label} className={`${m.bg} rounded-xl p-3 text-center border`}>
                                            {m.icon}
                                            <p className="text-lg font-bold">{m.actual}g</p>
                                            <div className="h-1 bg-white/30 dark:bg-black/20 rounded-full mt-1 mb-0.5 mx-2">
                                                <div className={`h-full rounded-full ${pct >= 80 ? 'bg-green-500' : pct >= 50 ? 'bg-amber-400' : 'bg-red-400'}`} style={{ width: `${pct}%` }} />
                                            </div>
                                            <p className="text-[9px] text-muted-foreground">{m.label} / {m.target}g</p>
                                        </div>
                                    );
                                })}
                            </div>
                        );
                    })()}

                    {/* Calorie Balance Bar */}
                    <Card className="border">
                        <CardContent className="p-3">
                            <p className="text-xs font-semibold mb-2 flex items-center gap-1.5">
                                <Dumbbell className="w-3.5 h-3.5 text-orange-500" />
                                {language === 'de' ? 'Kalorien-Bilanz' : 'Calorie Balance'}
                            </p>
                            <div className="space-y-1.5">
                                {/* Base metabolism */}
                                <div className="flex items-center gap-2">
                                    <span className="text-[10px] text-muted-foreground w-16 shrink-0">{language === 'de' ? 'Grundumsatz' : 'Base'}</span>
                                    <div className="flex-1 h-4 bg-muted rounded-full overflow-hidden">
                                        <div className="h-full bg-blue-400 rounded-full" style={{ width: `${Math.min(calorieGoal / (calorieGoal + sportCalories + 200) * 100, 100)}%` }} />
                                    </div>
                                    <span className="text-[10px] font-medium w-14 text-right">{calorieGoal}</span>
                                </div>
                                {/* Sport bonus */}
                                <div className="flex items-center gap-2">
                                    <span className="text-[10px] text-muted-foreground w-16 shrink-0 flex items-center gap-1">
                                        Sport
                                        <TDEEInfoButton />
                                    </span>
                                    <div className="flex-1 h-4 bg-muted rounded-full overflow-hidden">
                                        <div className="h-full bg-green-400 rounded-full" style={{ width: `${Math.min(sportCalories / (calorieGoal + sportCalories + 200) * 100, 100)}%` }} />
                                    </div>
                                    <span className="text-[10px] font-medium w-14 text-right text-green-600">+{sportCalories}</span>
                                </div>
                                {/* Consumed */}
                                <div className="flex items-center gap-2">
                                    <span className="text-[10px] text-muted-foreground w-16 shrink-0">{language === 'de' ? 'Gegessen' : 'Eaten'}</span>
                                    <div className="flex-1 h-4 bg-muted rounded-full overflow-hidden">
                                        <div className={`h-full rounded-full ${(summary?.totals?.calories || 0) > calorieGoal + sportCalories ? 'bg-red-400' : 'bg-orange-400'}`}
                                            style={{ width: `${Math.min((summary?.totals?.calories || 0) / (calorieGoal + sportCalories + 200) * 100, 100)}%` }} />
                                    </div>
                                    <span className="text-[10px] font-medium w-14 text-right">{summary?.totals?.calories || 0}</span>
                                </div>
                                {/* Balance */}
                                {(() => {
                                    const budget = calorieGoal + sportCalories;
                                    const eaten = summary?.totals?.calories || 0;
                                    const remaining = budget - eaten;
                                    return (
                                        <p className={`text-xs text-center font-medium mt-1 ${remaining >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                                            {remaining >= 0 
                                                ? `${remaining} kcal ${language === 'de' ? 'übrig' : 'remaining'}`
                                                : `${Math.abs(remaining)} kcal ${language === 'de' ? 'über Budget' : 'over budget'}`
                                            }
                                        </p>
                                    );
                                })()}
                            </div>
                        </CardContent>
                    </Card>

                    {/* Energy Account — cumulative balance with Nanouk fat-load visual */}
                    <EnergyBalanceCard refreshKey={`${summary?.totals?.calories || 0}-${sportCalories}`} />

                    {/* Drinks Section */}
                    <Card className="border" data-testid="drinks-section">
                        <CardContent className="p-4">
                            <p className="text-sm font-semibold mb-3 flex items-center gap-2">
                                <GlassWater className="w-4 h-4 icon-travel" />
                                {language === 'de' ? 'Getränke' : 'Drinks'}
                                <span className="text-xs font-normal text-muted-foreground ml-auto">
                                    {((summary?.totals?.water_ml || 0) / 1000).toFixed(1)}L / {waterGoal/1000}L
                                </span>
                            </p>
                            <Progress value={Math.min(waterProgress, 100)} className="h-1.5 mb-3" />
                            <div className="grid grid-cols-4 gap-2">
                                {drinks.map((drink) => (
                                    <button
                                        key={drink.id}
                                        onClick={() => handleLogDrink(drink)}
                                        disabled={drinkLoading === drink.id}
                                        className="flex flex-col items-center gap-0.5 p-2 rounded-xl border border-border hover:border-primary/30 hover:bg-primary/5 transition-all text-center"
                                        data-testid={`drink-${drink.id}`}
                                    >
                                        {drinkLoading === drink.id ? (
                                            <Loader2 className="w-5 h-5 animate-spin text-primary" />
                                        ) : (
                                            <span className="text-lg">{drink.icon}</span>
                                        )}
                                        <span className="text-[10px] font-medium leading-tight">{drink.name}</span>
                                        {drink.kcal > 0 && (
                                            <span className="text-[9px] text-muted-foreground">{drink.kcal} kcal</span>
                                        )}
                                    </button>
                                ))}
                            </div>
                        </CardContent>
                    </Card>

                    {/* Habit checklist — one-tap logging of frequent meals */}
                    <HabitMealChecklist date={selectedDate} onLogged={loadSummary} />

                    {/* Meals by Type - Accordion Style */}
                    <div className="space-y-2">
                        <h2 className="text-sm font-medium text-muted-foreground px-1">
                            {isToday 
                                ? (language === 'de' ? 'Heutige Mahlzeiten' : "Today's Meals")
                                : selectedDate.toLocaleDateString(language === 'de' ? 'de-DE' : 'en-US', { 
                                    weekday: 'long', 
                                    day: 'numeric', 
                                    month: 'long' 
                                })
                            }
                        </h2>
                        
                        {mealTypes.map((meal) => {
                            const entries = groupedMeals[meal.value] || [];
                            const totalCals = entries.reduce((sum, e) => sum + (e.calories || 0), 0);
                            const Icon = meal.icon;
                            const isExpanded = expandedMealType === meal.value;
                            
                            return (
                                <motion.div key={meal.value} className="rounded-xl overflow-hidden">
                                    {/* Meal Type Header */}
                                    <button
                                        onClick={() => setExpandedMealType(isExpanded ? null : meal.value)}
                                        className={`w-full flex items-center justify-between p-4 ${meal.bg} hover:brightness-110 transition-all`}
                                    >
                                        <div className="flex items-center gap-3">
                                            <div className={`w-10 h-10 rounded-full ${meal.bg} flex items-center justify-center`}>
                                                <Icon className={`w-5 h-5 ${meal.color}`} />
                                            </div>
                                            <div className="text-left">
                                                <p className="font-medium text-foreground">{meal.label}</p>
                                                <p className="text-sm text-muted-foreground">
                                                    {entries.length} {language === 'de' ? 'Einträge' : 'entries'} • {totalCals} kcal
                                                </p>
                                            </div>
                                        </div>
                                        <ChevronDown className={`w-5 h-5 text-muted-foreground transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                                    </button>
                                    
                                    {/* Entries */}
                                    <AnimatePresence>
                                        {isExpanded && (
                                            <motion.div
                                                initial={{ height: 0, opacity: 0 }}
                                                animate={{ height: 'auto', opacity: 1 }}
                                                exit={{ height: 0, opacity: 0 }}
                                                className="bg-muted/30"
                                            >
                                                {entries.length === 0 ? (
                                                    <p className="text-center text-muted-foreground py-6 text-sm">
                                                        {language === 'de' ? 'Noch keine Einträge' : 'No entries yet'}
                                                    </p>
                                                ) : (
                                                    <div className="divide-y divide-border">
                                                        {entries.map((entry) => (
                                                            <div key={entry.id}>
                                                                {editingEntry?.id === entry.id ? (
                                                                    /* Inline Edit Mode */
                                                                    <motion.div
                                                                        initial={{ opacity: 0 }}
                                                                        animate={{ opacity: 1 }}
                                                                        className="p-4 space-y-3 bg-muted"
                                                                    >
                                                                        <Input
                                                                            value={editingEntry.name}
                                                                            onChange={(e) => setEditingEntry({...editingEntry, name: e.target.value})}
                                                                            placeholder={language === 'de' ? 'Name' : 'Name'}
                                                                        />
                                                                        <div className="grid grid-cols-4 gap-2">
                                                                            <div>
                                                                                <label className="text-xs text-muted-foreground">kcal</label>
                                                                                <Input
                                                                                    type="number"
                                                                                    value={editingEntry.calories}
                                                                                    onChange={(e) => setEditingEntry({...editingEntry, calories: parseInt(e.target.value) || 0})}
                                                                                    className="h-9"
                                                                                />
                                                                            </div>
                                                                            <div>
                                                                                <label className="text-xs text-muted-foreground">P (g)</label>
                                                                                <Input
                                                                                    type="number"
                                                                                    value={editingEntry.protein}
                                                                                    onChange={(e) => setEditingEntry({...editingEntry, protein: parseFloat(e.target.value) || 0})}
                                                                                    className="h-9"
                                                                                />
                                                                            </div>
                                                                            <div>
                                                                                <label className="text-xs text-muted-foreground">C (g)</label>
                                                                                <Input
                                                                                    type="number"
                                                                                    value={editingEntry.carbs}
                                                                                    onChange={(e) => setEditingEntry({...editingEntry, carbs: parseFloat(e.target.value) || 0})}
                                                                                    className="h-9"
                                                                                />
                                                                            </div>
                                                                            <div>
                                                                                <label className="text-xs text-muted-foreground">F (g)</label>
                                                                                <Input
                                                                                    type="number"
                                                                                    value={editingEntry.fat}
                                                                                    onChange={(e) => setEditingEntry({...editingEntry, fat: parseFloat(e.target.value) || 0})}
                                                                                    className="h-9"
                                                                                />
                                                                            </div>
                                                                        </div>

                                                                        {/* Quick-Adjust: skaliert kcal + alle Makros gleichzeitig (v3.21) */}
                                                                        <div className="flex items-center gap-1.5 flex-wrap">
                                                                            <span className="text-[10px] text-muted-foreground mr-1">
                                                                                {language === 'de' ? 'Portion:' : 'Portion:'}
                                                                            </span>
                                                                            {[
                                                                                { f: 0.75, label: '–25%' },
                                                                                { f: 0.90, label: '–10%' },
                                                                                { f: 1.10, label: '+10%' },
                                                                                { f: 1.25, label: '+25%' },
                                                                            ].map(({ f, label }) => (
                                                                                <button
                                                                                    key={label}
                                                                                    type="button"
                                                                                    onClick={() => setEditingEntry({
                                                                                        ...editingEntry,
                                                                                        calories: Math.round((editingEntry.calories || 0) * f),
                                                                                        protein: Math.round((editingEntry.protein || 0) * f * 10) / 10,
                                                                                        carbs: Math.round((editingEntry.carbs || 0) * f * 10) / 10,
                                                                                        fat: Math.round((editingEntry.fat || 0) * f * 10) / 10,
                                                                                    })}
                                                                                    className={`px-2 py-1 text-[11px] font-semibold rounded-full border transition-colors ${
                                                                                        f < 1
                                                                                            ? 'border-blue-200 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-950/30'
                                                                                            : 'border-amber-200 text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-950/30'
                                                                                    }`}
                                                                                    data-testid={`quick-adjust-${label}`}
                                                                                >
                                                                                    {label}
                                                                                </button>
                                                                            ))}
                                                                            <button
                                                                                type="button"
                                                                                onClick={async () => {
                                                                                    try {
                                                                                        await axios.post(`${API}/favorites/meals`, {
                                                                                            name: editingEntry.name,
                                                                                            meal_type: editingEntry.meal_type || entry.meal_type || 'lunch',
                                                                                            calories: editingEntry.calories || 0,
                                                                                            protein: editingEntry.protein || 0,
                                                                                            carbs: editingEntry.carbs || 0,
                                                                                            fat: editingEntry.fat || 0,
                                                                                        });
                                                                                        toast.success(language === 'de' ? '⭐ Als Favorit gespeichert' : '⭐ Saved as favorite');
                                                                                    } catch (e) {
                                                                                        toast.error(language === 'de' ? 'Fehler beim Speichern' : 'Save failed');
                                                                                    }
                                                                                }}
                                                                                className="px-2 py-1 text-[11px] font-semibold rounded-full border border-yellow-200 text-yellow-600 hover:bg-yellow-50 dark:hover:bg-yellow-950/30 ml-auto"
                                                                                data-testid="save-favorite-btn"
                                                                                title={language === 'de' ? 'Als Favorit speichern' : 'Save as favorite'}
                                                                            >
                                                                                ⭐ {language === 'de' ? 'Favorit' : 'Favorite'}
                                                                            </button>
                                                                        </div>

                                                                        <div className="flex gap-2">
                                                                            <Button
                                                                                variant="outline"
                                                                                size="sm"
                                                                                onClick={() => setEditingEntry(null)}
                                                                                className="flex-1"
                                                                            >
                                                                                <X className="w-4 h-4 mr-1" />
                                                                                {language === 'de' ? 'Abbrechen' : 'Cancel'}
                                                                            </Button>
                                                                            <Button
                                                                                size="sm"
                                                                                onClick={() => handleUpdateFood(entry.id, editingEntry)}
                                                                                className="flex-1 bg-primary hover:bg-primary/90"
                                                                            >
                                                                                <Check className="w-4 h-4 mr-1" />
                                                                                {language === 'de' ? 'Speichern' : 'Save'}
                                                                            </Button>
                                                                        </div>
                                                                    </motion.div>
                                                                ) : (
                                                                    /* Normal View */
                                                                    <div 
                                                                        className="flex items-center gap-3 p-4 hover:bg-muted transition-colors cursor-pointer group"
                                                                        onClick={() => setEditingEntry({...entry})}
                                                                    >
                                                                        <div className="flex-1 min-w-0">
                                                                            <p className="font-medium text-foreground truncate">{entry.name}</p>
                                                                            <p className="text-sm text-muted-foreground">
                                                                                {entry.calories} kcal • {entry.protein}g P • {entry.carbs}g C • {entry.fat}g F
                                                                            </p>
                                                                        </div>
                                                                        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                                                            <Button
                                                                                variant="ghost"
                                                                                size="icon"
                                                                                className="h-8 w-8 text-muted-foreground hover:text-foreground"
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    setEditingEntry({...entry});
                                                                                }}
                                                                            >
                                                                                <Pencil className="w-4 h-4" />
                                                                            </Button>
                                                                            <Button
                                                                                variant="ghost"
                                                                                size="icon"
                                                                                className="h-8 w-8 text-muted-foreground hover:text-red-500"
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    handleDeleteFood(entry.id);
                                                                                }}
                                                                            >
                                                                                <Trash2 className="w-4 h-4" />
                                                                            </Button>
                                                                        </div>
                                                                        {/* Mobile: Always show hint */}
                                                                        <span className="text-xs text-muted-foreground sm:hidden">
                                                                            {language === 'de' ? 'Tippen zum Bearbeiten' : 'Tap to edit'}
                                                                        </span>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                                
                                                {/* Add to this meal type button */}
                                                <button
                                                    onClick={() => { setQuickLogMealType(meal.value); setQuickLogOpen(true); }}
                                                    className="w-full p-3 text-center text-sm text-primary hover:bg-primary/10 transition-colors"
                                                >
                                                    <Plus className="w-4 h-4 inline mr-1" />
                                                    {language === 'de' ? `${meal.label} hinzufügen` : `Add ${meal.label}`}
                                                </button>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </motion.div>
                            );
                        })}
                    </div>

                    {/* Empty State */}
                    {!loading && (!summary?.food_entries || summary.food_entries.length === 0) && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            className="text-center py-12"
                        >
                            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-secondary flex items-center justify-center">
                                <Apple className="w-10 h-10 text-muted-foreground" />
                            </div>
                            <h3 className="text-lg font-medium text-foreground mb-2">
                                {language === 'de' ? 'Noch keine Mahlzeiten' : 'No meals yet'}
                            </h3>
                            <p className="text-muted-foreground text-sm mb-4">
                                {language === 'de' 
                                    ? 'Tippe auf "+ Eintrag" um deine erste Mahlzeit zu erfassen'
                                    : 'Tap "+ Add" to log your first meal'
                                }
                            </p>
                            <Button 
                                onClick={() => setQuickLogOpen(true)}
                                className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
                            >
                                <Plus className="w-4 h-4 mr-2" />
                                {language === 'de' ? 'Erste Mahlzeit erfassen' : 'Log first meal'}
                            </Button>
                        </motion.div>
                    )}

                    {/* Trend section — averages over a chosen period (independent of selected day) */}
                    <div className="mt-2">
                        <NutritionTrendSection />
                    </div>
                </div>
            </motion.div>

            {/* Quick Log Drawer */}
            <QuickLogDrawer
                isOpen={quickLogOpen}
                onClose={() => setQuickLogOpen(false)}
                selectedDate={selectedDate}
                onSuccess={loadSummary}
                initialMealType={quickLogMealType}
            />

            {/* Food Scanner Dialog */}
            <FoodScanner 
                open={scannerOpen} 
                onOpenChange={setScannerOpen}
                onFoodScanned={handleFoodScanned}
            />

            {/* Barcode Scanner — reads EAN, looks up Open Food Facts + custom DB */}
            <BarcodeScannerSheet
                open={barcodeOpen}
                onOpenChange={setBarcodeOpen}
                onLogged={loadSummary}
            />

            {/* Price check — Taler & kcal BEFORE eating, nothing gets logged */}
            <PriceCheckSheet
                open={priceCheckOpen}
                onOpenChange={setPriceCheckOpen}
                onLogged={loadSummary}
            />
        </div>
    );
}
